import apiClient from '../../../api/core/apiClient';
import { getAuthToken } from '../../../lib/auth';
import {
    WhatsappContact,
    WhatsappMessage,
    PaginatedResponse,
    CursorPaginationDTO,
    MessageCursorResponse
} from '../types/whatsapp';

const BASE = '/whatsapp';

export const whatsappApi = {
    getContacts: async (params: CursorPaginationDTO = {}): Promise<PaginatedResponse<WhatsappContact>> => {
        return apiClient.get<PaginatedResponse<WhatsappContact>>(`${BASE}/contacts`, params);
    },

    getMessages: async (contactId: string, params: CursorPaginationDTO = {}): Promise<PaginatedResponse<WhatsappMessage>> => {
        return apiClient.get<PaginatedResponse<WhatsappMessage>>(`${BASE}/messages/${contactId}`, params);
    },

    getSearchMessagesCursors: async (contactId: string, params: { search: string }): Promise<MessageCursorResponse> => {
        return apiClient.get<MessageCursorResponse>(`${BASE}/messages/search/${contactId}`, params);
    },

    sendMessage: async (contactId: string, content: string, replyMessageId?: string, file?: File): Promise<any> => {
        if (file) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('contactId', contactId);
            if (content) {
                formData.append('content', content);
                formData.append('caption', content);
            }
            if (replyMessageId) {
                formData.append('replyMessageId', replyMessageId);
            }
            return await apiClient.post(`${BASE}/messages`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        }
        return await apiClient.post(`${BASE}/messages`, { content, contactId, replyMessageId });
    },

    markAsRead: async (contactId: string): Promise<any> => {
        return apiClient.patch(`${BASE}/messages/read/${contactId}`, {});
    },

    getAgent: async (): Promise<any> => {
        return apiClient.get(`/crm/agents`);
    },

    assignAgent: async (contactId: string, agentId: string): Promise<any> => {
        return apiClient.post(`${BASE}/conversation/escalate`, {
            agentId,
            conversationId: contactId
        });
    },

    getTemplates: async (): Promise<any> => {
        return apiClient.get(`${BASE}/templates`);
    },

    sendTemplate: async (template: string, criteria: any): Promise<any> => {
        return apiClient.post(`${BASE}/messages/template`, { template, criteria });
    },

    getProgressStream: (onMessage: (data: any) => void, onError?: (error: any) => void) => {
        const token = getAuthToken();
        const url = `${process.env.REACT_APP_API_URL || ''}whatsapp/messages/progress/stream`;
        const abortController = new AbortController();

        fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            },
            signal: abortController.signal
        }).then(async response => {
            if (!response.body) throw new Error('No readable stream');
            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const dataStr = line.slice(6).trim();
                        if (!dataStr) continue;
                        try {
                            const data = JSON.parse(dataStr);
                            onMessage(data);
                        } catch (e) {
                            // Ignore invalid JSON
                        }
                    }
                }
            }
        }).catch(err => {
            if (err.name === 'AbortError') return;
            if (onError) onError(err);
        });

        return () => abortController.abort();
    },
};
